import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BronzeHalfIcon,
  BronzeIcon,
  BronzeQuarterIcon,
  BronzeThreeQuarterIcon,
  DiamondIcon,
  EliteIcon,
  ElitePlusIcon,
  GoldIcon,
  InitialTierIcon,
  LegendIcon,
  PlatinumIcon,
  SilverIcon,
  UltimateIcon,
  UltraIcon,
} from '@pages/IntroducingBroker/assets/TierIcons'

import { Title1 } from '@ui/Typography'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { useWindowResize } from '@utils/domUtils'
import { sleep } from '@utils/transaction.utils'

import {
  IntroducingBrokerDetailsDto,
  LoyaltyBonusStatus,
  LoyaltyDetails,
  LoyaltyRewardDto,
  LoyaltyTier,
  LoyaltyTierDto,
} from '@model/IntroducingBrokerDetailsDto'
import { LoyaltyRewardsHistoryDto } from '@model/IntroducingBrokerLoyalty'

import { useHorizontalControlScroll } from '@hooks/useHorizontalControlScroll'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { SectionHeader } from '../Dashboard/components/SectionHeader/SectionHeader'
import { BigLoyaltyCard } from '../components/Loyalty/LoyaltyCard/BigLoyaltyCard'
import { LoyaltySliderControls } from '../components/Loyalty/LoyaltySliderControls'
import { LoyaltyTiersSlider } from '../components/Loyalty/LoyaltyTiersSlider'
import { ReportsTable } from './ReportsTable'
import { useLoyaltyTermsUrl } from './useLoyaltyTermsUrl'

import styles from './LoyaltyPage.module.scss'

export interface Props {
  loyaltyTiers?: LoyaltyDetails
  introducingBroker?: IntroducingBrokerDetailsDto
  rewardsHistoryQuery: PaginatedQueryResult<LoyaltyRewardsHistoryDto>
  onSubmitReward(reward: { rewardId: string; rewardTierLabel?: string | null }): Promise<void>
}
export interface LoyaltyTierMapped {
  id: number
  name: LoyaltyTier | null
  label: string | null
  rewardAmount: number
  ibReward: LoyaltyRewardDto | null
  imageUri: string | null
  isRedeemed: boolean
  isApproved: boolean
  isCreated: boolean
  isRejected: boolean
}

export const Loyalty: React.FC<Props> = ({
  loyaltyTiers,
  introducingBroker,
  rewardsHistoryQuery,
  onSubmitReward,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const termsUrl = useLoyaltyTermsUrl()
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const {
    containerRef: sliderRef,
    scroll,
    stopScrolling,
    showFadeLeft,
    showFadeRight,
    handleScroll,
    handleKeyDownScroll,
  } = useHorizontalControlScroll<HTMLDivElement>()

  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  const loyaltyTiersData = useMemo(() => {
    return (loyaltyTiers?.tiers || [])
      .map((loyaltyTier) => {
        const isRedeemed =
          loyaltyTier.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Redeemed
        const isCreated = loyaltyTier.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Created
        const isApproved =
          loyaltyTier.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Approved
        const isRejected =
          loyaltyTier.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Rejected

        return {
          id: loyaltyTier.id,
          name: loyaltyTier.name,
          label: loyaltyTier.label,
          rewardAmount: loyaltyTier.amount,
          ibReward: loyaltyTier.ibReward,
          imageUri: loyaltyTier.imageUri,
          isRedeemed,
          isApproved,
          isCreated,
          isRejected,
        }
      })
      .filter((loyaltyTier) => loyaltyTier.id !== 0 || loyaltyTier.rewardAmount !== 0)
  }, [loyaltyTiers])

  const isFinalTierRedeemed = useMemo(() => {
    const ultraTierId = 10
    return loyaltyTiers?.loyaltyRewards.some(
      (reward) =>
        reward.tierId === ultraTierId &&
        reward.loyaltyBonusStatus.id === LoyaltyBonusStatus.Redeemed
    )
  }, [loyaltyTiers?.loyaltyRewards])

  const submitReward = useCallback(
    async (tier?: LoyaltyTierDto | LoyaltyTierMapped | null) => {
      const reward = introducingBroker?.loyaltyDetails.redeemableRewards.find(
        (x) => x.tierId === tier?.ibReward?.tierId
      )
      if (reward && reward.id) {
        if (reward.loyaltyBonusStatus.id === LoyaltyBonusStatus.Approved) {
          setIsLoading(true)
          await onSubmitReward({ rewardId: reward.id, rewardTierLabel: tier?.label })
        } else {
          console.error('Reward not approved')
        }
      } else {
        console.error('Reward not found')
      }
      await sleep(3000)
      setIsLoading(false)
    },
    [introducingBroker?.loyaltyDetails.redeemableRewards, onSubmitReward]
  )

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    rewardsHistoryQuery.setPageQuery?.({
      ...rewardsHistoryQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  useEffect(() => {
    const container = sliderRef.current
    const claimedCardIndex =
      loyaltyTiers?.tiers?.findLastIndex(
        (tier) => tier?.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Redeemed
      ) ?? -1

    if (claimedCardIndex === -1) {
      return
    }

    let nextUnClaimedCardIndex = 0

    if (claimedCardIndex > -1) {
      const nextIndex = claimedCardIndex
      nextUnClaimedCardIndex = nextIndex < cardRefs.current.length ? nextIndex : claimedCardIndex
    }

    const card = cardRefs.current[nextUnClaimedCardIndex]

    if (container && card) {
      const cardLeft = card.offsetLeft
      const cardWidth = card.offsetWidth
      const percentToHideFirstVisibleCard = 0.6

      const scrollOffset = cardLeft - cardWidth * percentToHideFirstVisibleCard

      const maxScroll = container.scrollWidth - container.offsetWidth
      const finalScroll = Math.min(scrollOffset, maxScroll)

      container.scrollTo({
        left: finalScroll > 0 ? finalScroll : 0,
        behavior: 'smooth',
      })
    }
  }, [loyaltyTiers, sliderRef])

  return (
    <>
      <main className={styles.wrapper}>
        <SectionHeader
          title={t('Loyalty Program.Bonus program')}
          seeMoreLinkProps={{
            to: termsUrl,
            target: '_blank',
            variant: 'tertiary',
            children: isMobile ? t('More') : t('More Information'),
            iconRight: ArrowCircleIcon.UpRight,
          }}
          id={ScrollToIds.LoyaltyProgramHeader}
        />
        <Loading isLoading={isLoading} showLoadingIcon wrapperClass={styles.loadingWrapper}>
          {loyaltyTiers && (
            <div className={styles.container}>
              <BigLoyaltyCard
                loyaltyTiers={loyaltyTiers}
                onSubmitReward={submitReward}
                isLoading={isLoading}
              />
              <section className={styles.tiers}>
                <div className={styles.sliderHeader}>
                  {(loyaltyTiers?.nextTier || !isFinalTierRedeemed) && (
                    <Title1 className={styles.title}>
                      {t('Loyalty Program.Youre almost there')}
                    </Title1>
                  )}
                  <LoyaltySliderControls
                    onScroll={scroll}
                    onHandleKeyDownScroll={handleKeyDownScroll}
                    stopScrolling={stopScrolling}
                  />
                </div>
                <LoyaltyTiersSlider
                  showFadeLeft={showFadeLeft}
                  showFadeRight={showFadeRight}
                  handleScroll={handleScroll}
                  onSubmitReward={submitReward}
                  loyaltyTiers={loyaltyTiersData}
                  sliderRef={sliderRef}
                  cardRefs={cardRefs}
                />
              </section>
            </div>
          )}
        </Loading>

        <section className={styles.reports}>
          <SectionHeader title={t('Tabs.Reports')} id={ScrollToIds.ReportsHeader} />
          <ReportsTable tableQuery={rewardsHistoryQuery} />
          {rewardsHistoryQuery.meta && rewardsHistoryQuery.hasInitialResults && (
            <Pagination
              scrollToHeaderId={ScrollToIds.IBRebateClientsHeader}
              maxPageSize={100}
              pageData={rewardsHistoryQuery.meta}
              isLoading={rewardsHistoryQuery.isLoading}
              onPageChanged={handlePageChange}
            />
          )}
        </section>
      </main>
    </>
  )
}

export const tierIconByType: Record<number, React.ElementType> = {
  0: InitialTierIcon,
  1: BronzeIcon,
  2: SilverIcon,
  3: GoldIcon,
  4: PlatinumIcon,
  5: DiamondIcon,
  6: EliteIcon,
  7: ElitePlusIcon,
  8: LegendIcon,
  9: UltimateIcon,
  10: UltraIcon,
  101: BronzeQuarterIcon,
  102: BronzeHalfIcon,
  103: BronzeThreeQuarterIcon,
}
